import { useState, useEffect, useMemo } from "react";
import { Icons } from "./Icons";
import { fmt, fmtPrice } from "./Shared";
import { searchCards, getPokemonSets, getPokemonCardsBySet, getOnePieceSets, getOnePieceCardsBySet, setPokemonLang, POKEMON_LANGUAGES } from "../api/cardSearch";
import { getLatestPrice, getPriceSource } from "../api/priceService";
import { getEbayPriceSummary, fetchEbayBulkPrices } from "../api/ebayService";
import { CURRENCIES } from "../api/currencyService";

// Rarity color mapping for tags
const RARITY_COLORS = {
  // Pokémon
  "Common": { bg: "rgba(140,160,190,0.15)", color: "#8DA4C4" },
  "Uncommon": { bg: "rgba(100,200,130,0.15)", color: "#64C882" },
  "Rare": { bg: "rgba(255,215,0,0.15)", color: "#FFD700" },
  "Rare Holo": { bg: "rgba(255,215,0,0.2)", color: "#FFD700" },
  "Double Rare": { bg: "rgba(255,180,0,0.2)", color: "#FFB400" },
  "Ultra Rare": { bg: "rgba(219,0,0,0.15)", color: "#DB0000" },
  "Illustration Rare": { bg: "rgba(160,100,255,0.15)", color: "#A064FF" },
  "Special Art Rare": { bg: "rgba(255,100,200,0.15)", color: "#FF64C8" },
  "Hyper Rare": { bg: "rgba(255,215,0,0.25)", color: "#FFD700" },
  "Rare Holo V": { bg: "rgba(59,139,245,0.15)", color: "#3B8BF5" },
  "Rare Holo VMAX": { bg: "rgba(160,100,255,0.15)", color: "#A064FF" },
  "Rare Holo VSTAR": { bg: "rgba(255,180,0,0.15)", color: "#FFB400" },
  "ACE SPEC Rare": { bg: "rgba(219,0,0,0.15)", color: "#DB0000" },
  "Rare Holo EX": { bg: "rgba(59,139,245,0.15)", color: "#3B8BF5" },
  "Rare Holo GX": { bg: "rgba(59,139,245,0.15)", color: "#3B8BF5" },
  "Rare Ultra": { bg: "rgba(219,0,0,0.15)", color: "#DB0000" },
  "Rare Secret": { bg: "rgba(255,215,0,0.25)", color: "#FFD700" },
  "Rare Rainbow": { bg: "rgba(255,100,200,0.2)", color: "#FF64C8" },
  // One Piece
  "L": { bg: "rgba(219,0,0,0.15)", color: "#DB0000" },
  "C": { bg: "rgba(140,160,190,0.15)", color: "#8DA4C4" },
  "UC": { bg: "rgba(100,200,130,0.15)", color: "#64C882" },
  "R": { bg: "rgba(59,139,245,0.15)", color: "#3B8BF5" },
  "SR": { bg: "rgba(255,180,0,0.2)", color: "#FFB400" },
  "SEC": { bg: "rgba(255,215,0,0.25)", color: "#FFD700" },
  "SP": { bg: "rgba(255,100,200,0.15)", color: "#FF64C8" },
  "P": { bg: "rgba(160,100,255,0.15)", color: "#A064FF" },
  "TR": { bg: "rgba(100,200,130,0.15)", color: "#64C882" },
};

const getRarityStyle = (rarity) => RARITY_COLORS[rarity] || { bg: "rgba(140,160,190,0.1)", color: "#8DA4C4" };

const AddCardPage = ({ onSave, onNavigate, editItem, currency = "USD", exchangeRates = {} }) => {
  const fmtC = (n) => fmtPrice(n, currency, exchangeRates);
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("none");
  const [cardLang, setCardLang] = useState("en"); // Pokémon card language
  const [rarityFilter, setRarityFilter] = useState("all");
  const [selectedCard, setSelectedCard] = useState(editItem?.card_data || null);
  const [setCardSearch, setSetCardSearch] = useState("");
  const [ebayData, setEbayData] = useState(null);
  const [ebayLoading, setEbayLoading] = useState(false);

  // Fetch eBay price when a card is selected and has no TCGPlayer/Cardmarket price
  useEffect(() => {
    if (!selectedCard) { setEbayData(null); return; }
    if (getLatestPrice(selectedCard) > 0) { setEbayData(null); return; } // Already has price
    setEbayLoading(true);
    getEbayPriceSummary(selectedCard).then((data) => {
      setEbayData(data);
      setEbayLoading(false);
    }).catch(() => setEbayLoading(false));
  }, [selectedCard?.id]);

  const [quantity, setQuantity] = useState(editItem ? String(editItem.quantity) : "1");
  const [purchasePrice, setPurchasePrice] = useState(editItem ? String(editItem.purchase_price) : "");
  const [purchaseCurrency, setPurchaseCurrency] = useState(currency || "USD"); // Currency for purchase price input
  const [purchaseDate, setPurchaseDate] = useState(editItem ? editItem.purchase_date : new Date().toISOString().split("T")[0]);
  const [condition, setCondition] = useState(editItem ? editItem.condition : "Near Mint");
  const [language, setLanguage] = useState(editItem ? editItem.language : "English");
  const [notes, setNotes] = useState(editItem ? editItem.notes || "" : "");
  const [priceFilter, setPriceFilter] = useState("all"); // "all" | "under5" | "5to20" | "20to50" | "over50"

  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);

  const [pokemonSets, setPokemonSets] = useState([]);
  const [onePieceSets, setOnePieceSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [setsLoaded, setSetsLoaded] = useState(false);
  const [selectedSet, setSelectedSet] = useState(null);
  const [setCards, setSetCards] = useState([]);  // ALL cards from selected set (unfiltered)
  const [setCardsLoading, setSetCardsLoading] = useState(false);
  const [setTotalCards, setSetTotalCards] = useState(0);
  const [browseSetPage, setBrowseSetPage] = useState(1);
  const [setSearchFilter, setSetSearchFilter] = useState("");

  // ── eBay price cache for grid cards ──
  const [ebayCache, setEbayCache] = useState({});
  const [ebayFetching, setEbayFetching] = useState(false);
  const [priceSortOrder, setPriceSortOrder] = useState("none");

  // Bulk fetch eBay prices for all cards without price — few requests total
  const visibleCards = selectedSet ? setCards : searchResults;
  useEffect(() => {
    if (visibleCards.length === 0) return;
    const needEbay = visibleCards.filter((c) => getLatestPrice(c) === 0 && ebayCache[c.id] === undefined);
    if (needEbay.length === 0) return;

    setEbayFetching(true);
    let cancelled = false;
    fetchEbayBulkPrices(needEbay).then((priceMap) => {
      if (!cancelled) {
        setEbayCache((prev) => ({ ...prev, ...priceMap }));
        setEbayFetching(false);
      }
    }).catch(() => {
      if (!cancelled) setEbayFetching(false);
    });
    return () => { cancelled = true; };
  }, [visibleCards.length, selectedSet?.id, searchDone]);

  // Get price for a card (with eBay cache fallback)
  const getCardPrice = (card) => {
    const base = getLatestPrice(card);
    if (base > 0) return { price: base, source: getPriceSource(card) };
    const ebay = ebayCache[card.id];
    if (ebay && ebay > 0) return { price: ebay, source: "eBay" };
    return { price: 0, source: null };
  };

  // Available rarities: ONLY from actually loaded cards
  const availableSetRarities = useMemo(() => {
    if (setCards.length === 0) return [];
    const rarities = [...new Set(setCards.map((c) => c.rarity).filter(Boolean))];
    return rarities.sort();
  }, [setCards]);

  const availableSearchRarities = useMemo(() => {
    if (searchResults.length === 0) return [];
    const rarities = [...new Set(searchResults.map((c) => c.rarity).filter(Boolean))];
    return rarities.sort();
  }, [searchResults]);

  // Load sets
  useEffect(() => {
    if (gameFilter === "none" || setsLoaded) return;
    setSetsLoading(true);
    Promise.all([getPokemonSets().catch(() => []), getOnePieceSets().catch(() => [])]).then(([pkmn, op]) => {
      setPokemonSets(pkmn);
      setOnePieceSets(Array.isArray(op) ? op.map((s) => ({ id: s.set_id || s.id || "", name: s.set_name || s.name || "", game: "one_piece" })) : []);
      setSetsLoading(false); setSetsLoaded(true);
    });
  }, [gameFilter, setsLoaded]);

  // Name search
  useEffect(() => {
    if (gameFilter === "none" || !search || search.trim().length < 2) {
      setSearchResults([]); setSearchDone(false); setTotalResults(0); return;
    }
    const timer = setTimeout(async () => {
      setSearching(true); setSearchDone(false);
      try {
        const result = await searchCards(search, gameFilter === "all" ? "all" : gameFilter, page, 48);
        setSearchResults(result.cards); setTotalResults(result.totalCount);
      } catch { setSearchResults([]); }
      setSearching(false); setSearchDone(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, gameFilter, page, cardLang]);

  // Load ALL set cards (no rarity filter on API — filter client-side)
  useEffect(() => {
    if (!selectedSet) return;
    setSetCardsLoading(true); setSetCards([]);
    (async () => {
      try {
        let result;
        if (selectedSet.game === "pokemon") {
          // Load up to 250 cards (Pokémon API max pageSize)
          result = await getPokemonCardsBySet(selectedSet.id, browseSetPage, 250);
        } else {
          result = await getOnePieceCardsBySet(selectedSet.id, 1, 500);
        }
        setSetCards(result.cards); setSetTotalCards(result.totalCount);
      } catch { setSetCards([]); }
      setSetCardsLoading(false);
    })();
  }, [selectedSet, browseSetPage]);

  // Price filter helper (uses eBay cache)
  const getFilterPrice = (c) => {
    return getCardPrice(c).price;
  };

  const applyPriceFilter = (cards) => {
    let list = cards;
    if (priceFilter !== "all") {
      list = list.filter((c) => {
        const p = getFilterPrice(c);
        if (priceFilter === "under5") return p > 0 && p < 5;
        if (priceFilter === "5to20") return p >= 5 && p <= 20;
        if (priceFilter === "20to50") return p > 20 && p <= 50;
        if (priceFilter === "over50") return p > 50;
        if (priceFilter === "noPrice") return p === 0;
        return true;
      });
    }
    // Apply price sort
    if (priceSortOrder === "asc") {
      list = [...list].sort((a, b) => getFilterPrice(a) - getFilterPrice(b));
    } else if (priceSortOrder === "desc") {
      list = [...list].sort((a, b) => getFilterPrice(b) - getFilterPrice(a));
    }
    return list;
  };

  // Client-side filtering (rarity + search + price within set)
  const filteredSetCards = useMemo(() => {
    let list = setCards;
    if (rarityFilter !== "all") list = list.filter((c) => c.rarity === rarityFilter);
    if (setCardSearch.trim().length >= 2) {
      const q = setCardSearch.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.card_number.toLowerCase().includes(q));
    }
    list = applyPriceFilter(list);
    return list;
  }, [setCards, rarityFilter, setCardSearch, priceFilter, ebayCache, priceSortOrder]);

  // Client-side rarity + price filter for search results
  const filteredSearchResults = useMemo(() => {
    let list = searchResults;
    if (rarityFilter !== "all") list = list.filter((c) => c.rarity === rarityFilter);
    list = applyPriceFilter(list);
    return list;
  }, [searchResults, rarityFilter, priceFilter, ebayCache, priceSortOrder]);

  const showPokemonSets = (gameFilter === "all" || gameFilter === "pokemon");
  const showOnePieceSets = (gameFilter === "all" || gameFilter === "one_piece");
  const filteredPkmn = pokemonSets.filter((s) => {
    if (!setSearchFilter) return true;
    const q = setSearchFilter.toLowerCase();
    return (s.name || "").toLowerCase().includes(q) || (s.name_en || "").toLowerCase().includes(q) || (s.name_local || "").toLowerCase().includes(q);
  });
  const filteredOP = onePieceSets.filter((s) => !setSearchFilter || s.name.toLowerCase().includes(setSearchFilter.toLowerCase()));

  const handleGameSelect = (g) => {
    setGameFilter(g); setSelectedSet(null); setSetCards([]);
    setSearch(""); setSearchResults([]); setSearchDone(false);
    setPage(1); setBrowseSetPage(1); setRarityFilter("all"); setSetCardSearch(""); setPriceFilter("all"); setPriceSortOrder("none");
  };

  const handleLangChange = (lang) => {
    setCardLang(lang);
    setPokemonLang(lang);
    // Reset sets to reload with new language
    setSetsLoaded(false);
    setPokemonSets([]);
    // Reset set cards if a set is selected (will reload with new lang)
    if (selectedSet) {
      setSetCards([]);
      setSetCardsLoading(true);
      // Re-fetch set cards in new language
      (async () => {
        try {
          let result;
          if (selectedSet.game === "pokemon") {
            result = await getPokemonCardsBySet(selectedSet.id, 1, 250);
          } else {
            result = await getOnePieceCardsBySet(selectedSet.id, 1, 500);
          }
          setSetCards(result.cards);
          setSetTotalCards(result.totalCount);
        } catch { setSetCards([]); }
        setSetCardsLoading(false);
      })();
    }
    // Re-trigger search if active
    if (search && search.trim().length >= 2) {
      setSearchDone(false);
      setSearchResults([]);
    }
  };

  const handleSave = () => {
    if (!selectedCard) return;
    // Convert purchase price to USD for storage
    let priceInUSD = parseFloat(purchasePrice) || 0;
    if (purchaseCurrency !== "USD" && exchangeRates[purchaseCurrency]) {
      priceInUSD = priceInUSD / exchangeRates[purchaseCurrency];
    }
    priceInUSD = Math.round(priceInUSD * 100) / 100;

    onSave({
      id: editItem ? editItem.id : "ci_" + Date.now(), card_id: selectedCard.id,
      card_data: {
        id: selectedCard.id, api_id: selectedCard.api_id, name: selectedCard.name,
        game: selectedCard.game, set_name: selectedCard.set_name, set_code: selectedCard.set_code,
        card_number: selectedCard.card_number, rarity: selectedCard.rarity, variant: selectedCard.variant,
        image_url: selectedCard.image_url, image_small: selectedCard.image_small || "", slug: selectedCard.slug,
        tcgplayer_prices: selectedCard.tcgplayer_prices || null, cardmarket_prices: selectedCard.cardmarket_prices || null,
        market_price: selectedCard.market_price || null, inventory_price: selectedCard.inventory_price || null,
        card_color: selectedCard.card_color || "", card_type: selectedCard.card_type || "", card_power: selectedCard.card_power || "",
      },
      quantity: parseInt(quantity) || 1,
      purchase_price: priceInUSD,
      purchase_currency: purchaseCurrency, // Save original currency for reference
      purchase_price_original: parseFloat(purchasePrice) || 0, // Save original amount
      purchase_date: purchaseDate, condition, language, notes,
      created_date: editItem ? editItem.created_date : new Date().toISOString(),
      updated_date: new Date().toISOString(),
    });
  };

  // ── Rarity Tag ──
  const RarityTag = ({ rarity }) => {
    if (!rarity) return null;
    const style = getRarityStyle(rarity);
    return (
      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: style.bg, color: style.color, letterSpacing: 0.3 }}>
        {rarity}
      </span>
    );
  };

  // ── Card Grid ──
  const CardGrid = ({ cards, total, currentPage, onPageChange, pageSize = 36 }) => (
    <>
      <div className="catalog-grid">
        {cards.map((card) => {
          const { price, source } = getCardPrice(card);
          const isEbay = source === "eBay";
          const isLoading = price === 0 && getLatestPrice(card) === 0 && !ebayCache.hasOwnProperty(card.id);
          return (
            <div key={card.id} className="catalog-card" onClick={() => setSelectedCard(card)}>
              {card.image_url
                ? <img src={card.image_small || card.image_url} alt={card.name} className="catalog-card-img" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
                : <div className="catalog-card-placeholder"><span className="game-icon">{card.game === "pokemon" ? "⚡" : "🏴‍☠️"}</span><span className="card-num">{card.card_number}</span></div>}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, marginBottom: 4 }}>
                <div className="catalog-card-name" style={{ flex: 1, marginRight: 6 }}>{card.name}</div>
                <RarityTag rarity={card.rarity} />
              </div>
              <div className="catalog-card-set">{card.set_name} · #{card.card_number}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
                {isLoading ? (
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>...</span>
                ) : (
                  <>
                    <span className="catalog-card-price" style={{ margin: 0 }}>{price > 0 ? fmtC(price) : "—"}</span>
                    {price > 0 && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 8, color: isEbay ? "var(--amber)" : "var(--green)", background: isEbay ? "var(--amber-soft)" : "var(--green-soft)" }}>{source}</span>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {total > pageSize && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 24 }}>
          <button className="btn btn-secondary btn-sm" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>← Previous</button>
          <span style={{ fontSize: 13, color: "var(--text-secondary)", display: "flex", alignItems: "center" }}>Page {currentPage} of {Math.ceil(total / pageSize)}</span>
          <button className="btn btn-secondary btn-sm" disabled={currentPage >= Math.ceil(total / pageSize)} onClick={() => onPageChange(currentPage + 1)}>Next →</button>
        </div>
      )}
    </>
  );

  // ── Rarity Filter Bar (only available rarities) ──
  const RarityBar = ({ rarities }) => {
    if (!rarities || rarities.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Filter by Rarity</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className={`filter-chip ${rarityFilter === "all" ? "active" : ""}`} onClick={() => setRarityFilter("all")} style={{ fontSize: 12, padding: "4px 12px" }}>
            All ({selectedSet ? setCards.length : searchResults.length})
          </button>
          {rarities.map((r) => {
            const count = (selectedSet ? setCards : searchResults).filter((c) => c.rarity === r).length;
            const style = getRarityStyle(r);
            return (
              <button
                key={r}
                className={`filter-chip ${rarityFilter === r ? "active" : ""}`}
                onClick={() => setRarityFilter(r)}
                style={{
                  fontSize: 12, padding: "4px 12px",
                  ...(rarityFilter !== r ? { borderColor: style.color + "40", color: style.color } : {})
                }}
              >
                {r} ({count})
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Price Filter Bar ──
  const PriceFilterBar = ({ cards }) => {
    if (!cards || cards.length === 0) return null;
    const counts = {
      all: cards.length,
      under5: cards.filter((c) => { const p = getFilterPrice(c); return p > 0 && p < 5; }).length,
      "5to20": cards.filter((c) => { const p = getFilterPrice(c); return p >= 5 && p <= 20; }).length,
      "20to50": cards.filter((c) => { const p = getFilterPrice(c); return p > 20 && p <= 50; }).length,
      over50: cards.filter((c) => getFilterPrice(c) > 50).length,
      noPrice: cards.filter((c) => getFilterPrice(c) === 0).length,
    };
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Filter by Price</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { key: "all", label: "All" },
            { key: "under5", label: "< $5" },
            { key: "5to20", label: "$5–$20" },
            { key: "20to50", label: "$20–$50" },
            { key: "over50", label: "$50+" },
            { key: "noPrice", label: "No Price" },
          ].filter((f) => f.key === "all" || counts[f.key] > 0).map((f) => (
            <button key={f.key} className={`filter-chip ${priceFilter === f.key ? "active" : ""}`} onClick={() => setPriceFilter(f.key)} style={{ fontSize: 12, padding: "4px 12px" }}>{f.label} ({counts[f.key]})</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", marginRight: 4 }}>Sort</div>
          <button className={`filter-chip ${priceSortOrder === "asc" ? "active" : ""}`} onClick={() => setPriceSortOrder(priceSortOrder === "asc" ? "none" : "asc")} style={{ fontSize: 12, padding: "4px 12px" }}>Price ↑</button>
          <button className={`filter-chip ${priceSortOrder === "desc" ? "active" : ""}`} onClick={() => setPriceSortOrder(priceSortOrder === "desc" ? "none" : "desc")} style={{ fontSize: 12, padding: "4px 12px" }}>Price ↓</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => onNavigate("collection")} style={{ marginBottom: 8 }}><Icons.Back /> Back to Collection</button>
        <h1 className="page-title">{editItem ? "Edit Card" : "Add Card to Collection"}</h1>
        <p className="page-subtitle">{selectedCard ? `Selected: ${selectedCard.name}` : "Choose a game, then search or browse by collection"}</p>
      </div>

      {!editItem && (
        <>
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            {[{ key: "all", label: "All Games" }, { key: "pokemon", label: "⚡ Pokémon" }, { key: "one_piece", label: "🏴‍☠️ One Piece" }].map((g) => (
              <button key={g.key} className={`filter-chip ${gameFilter === g.key ? "active" : ""}`} onClick={() => handleGameSelect(g.key)} style={{ padding: "8px 18px", fontSize: 14 }}>{g.label}</button>
            ))}
          </div>

          {/* Language selector for Pokémon */}
          {(gameFilter === "pokemon" || gameFilter === "all") && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Language</span>
              {POKEMON_LANGUAGES.map((l) => (
                <button key={l.code} className={`filter-chip ${cardLang === l.code ? "active" : ""}`} onClick={() => handleLangChange(l.code)} style={{ fontSize: 12, padding: "4px 10px" }}>
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
          )}

          {gameFilter === "none" && (
            <div className="empty-state"><div style={{ fontSize: 48, marginBottom: 16 }}>🎮</div><h3>Choose a game to start</h3><p>Select Pokémon, One Piece, or All Games to search cards and browse collections.</p></div>
          )}

          {/* Game selected, no set */}
          {gameFilter !== "none" && !selectedSet && (
            <>
              <div className="search-bar"><Icons.Search /><input className="input" placeholder={gameFilter === "pokemon" ? "Search Pokémon cards..." : gameFilter === "one_piece" ? "Search One Piece cards..." : "Search all cards..."} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); setRarityFilter("all"); }} autoFocus /></div>

              {searchResults.length > 0 && <RarityBar rarities={availableSearchRarities} />}
              {searchResults.length > 0 && <PriceFilterBar cards={searchResults} />}

              {searching && <div style={{ textAlign: "center", padding: 40, color: "var(--text-secondary)" }}><Icons.Spinner /><p style={{ marginTop: 12 }}>Searching cards...</p></div>}

              {!searching && filteredSearchResults.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>{filteredSearchResults.length} {rarityFilter !== "all" ? rarityFilter + " " : ""}cards for "{search}"</div>
                  <CardGrid cards={filteredSearchResults} total={filteredSearchResults.length} currentPage={1} onPageChange={() => {}} pageSize={200} />
                </>
              )}

              {!searching && searchDone && filteredSearchResults.length === 0 && <div className="empty-state" style={{ marginBottom: 24 }}><h3>No cards found</h3><p>Try a different term or rarity, or browse collections below.</p></div>}

              {(!search || search.length < 2) && (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 14 }}>📦 Browse by Collection</div>
                  <div className="search-bar" style={{ marginBottom: 16 }}><Icons.Search /><input className="input" placeholder="Filter collections..." value={setSearchFilter} onChange={(e) => setSetSearchFilter(e.target.value)} /></div>
                  {setsLoading && <div style={{ textAlign: "center", padding: 30, color: "var(--text-secondary)" }}><Icons.Spinner /><p style={{ marginTop: 8 }}>Loading collections...</p></div>}
                  {!setsLoading && (
                    <>
                      {showPokemonSets && filteredPkmn.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "var(--pokemon-gold)" }}>⚡ Pokémon ({filteredPkmn.length} sets)</h3>
                          <div className="set-grid">{filteredPkmn.slice(0, 50).map((s) => (
                            <div key={s.id} className="set-item" onClick={() => { setSelectedSet({ ...s, game: "pokemon" }); setBrowseSetPage(1); setSetCardSearch(""); setRarityFilter("all"); }}>
                              {s.images?.symbol && <img src={s.images.symbol} alt="" style={{ width: 24, height: 24 }} onError={(e) => e.target.style.display = "none"} />}
                              {s.isExclusive && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "var(--amber-soft)", color: "var(--amber)", fontWeight: 700 }}>JP</span>}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name_en || s.name}</div>
                                {s.name_local && s.name_local !== (s.name_en || s.name) && <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{s.name_local}</div>}
                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.total} cards{s.releaseDate ? ` · ${s.releaseDate}` : ""}{s.series ? ` · ${s.series}` : ""}</div>
                              </div>
                              <Icons.ChevronRight />
                            </div>
                          ))}</div>
                          {filteredPkmn.length > 50 && <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>Showing 50 of {filteredPkmn.length}. Use filter above.</p>}
                        </div>
                      )}
                      {showOnePieceSets && filteredOP.length > 0 && (
                        <div>
                          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "var(--highlight)" }}>🏴‍☠️ One Piece ({filteredOP.length} sets)</h3>
                          <div className="set-grid">{filteredOP.map((s) => (
                            <div key={s.id} className="set-item" onClick={() => { setSelectedSet({ ...s, game: "one_piece" }); setBrowseSetPage(1); setSetCardSearch(""); setRarityFilter("all"); }}>
                              <div style={{ width: 24, height: 24, borderRadius: 4, background: "var(--highlight-soft)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🏴‍☠️</div>
                              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div><div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.id}</div></div>
                              <Icons.ChevronRight />
                            </div>
                          ))}</div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* Set cards */}
          {selectedSet && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedSet(null); setSetCards([]); setRarityFilter("all"); setSetCardSearch(""); }} style={{ marginBottom: 16 }}><Icons.Back /> Back to Collections</button>
              <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <h3 style={{ fontSize: 18, fontWeight: 700 }}>{selectedSet.name}</h3>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{setTotalCards} cards total</span>
                <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: selectedSet.game === "pokemon" ? "rgba(255,215,0,0.1)" : "var(--highlight-soft)", color: selectedSet.game === "pokemon" ? "var(--pokemon-gold)" : "var(--highlight)", border: "1px solid", borderColor: selectedSet.game === "pokemon" ? "rgba(255,215,0,0.25)" : "rgba(219,0,0,0.25)", fontWeight: 600 }}>
                  {selectedSet.game === "pokemon" ? "Pokémon" : "One Piece"}
                </span>
              </div>
              <div className="search-bar"><Icons.Search /><input className="input" placeholder={`Search within ${selectedSet.name}...`} value={setCardSearch} onChange={(e) => setSetCardSearch(e.target.value)} /></div>

              {/* Rarity filter — only shows rarities that exist in this set */}
              {!setCardsLoading && <RarityBar rarities={availableSetRarities} />}
              {!setCardsLoading && setCards.length > 0 && <PriceFilterBar cards={setCards} />}

              {setCardsLoading && <div style={{ textAlign: "center", padding: 40 }}><Icons.Spinner /><p style={{ marginTop: 12, color: "var(--text-secondary)" }}>Loading cards...</p></div>}
              {!setCardsLoading && filteredSetCards.length > 0 && (
                <>
                  {(rarityFilter !== "all" || setCardSearch) && <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>Showing {filteredSetCards.length} cards{rarityFilter !== "all" ? ` · ${rarityFilter}` : ""}{setCardSearch ? ` · "${setCardSearch}"` : ""}</div>}
                  <CardGrid cards={filteredSetCards} total={filteredSetCards.length} currentPage={1} onPageChange={() => {}} pageSize={500} />
                </>
              )}
              {!setCardsLoading && filteredSetCards.length === 0 && <div className="empty-state"><h3>No matching cards</h3><p>Try adjusting your filters.</p></div>}
            </>
          )}
        </>
      )}

      {/* Purchase Modal */}
      {(selectedCard || editItem) && (
        <div className="purchase-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !editItem) setSelectedCard(null); }}>
          <div className="purchase-modal">
            <div className="purchase-modal-header">
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editItem ? "Edit Card" : "Add to Collection"}</h3>
              {!editItem && <button className="btn btn-ghost btn-sm" onClick={() => setSelectedCard(null)} style={{ fontSize: 18, padding: 4 }}>✕</button>}
            </div>
            <div className="purchase-modal-body">
              <div className="add-card-detail">
                {selectedCard && (
                  <div className="add-card-preview">
                    {selectedCard.image_url
                      ? <img src={selectedCard.image_url} alt={selectedCard.name} style={{ width: "100%", borderRadius: 12, border: "2px solid var(--border)" }} loading="lazy" />
                      : <div className="catalog-card-placeholder" style={{ height: 300 }}><span className="game-icon">{selectedCard.game === "pokemon" ? "⚡" : "🏴‍☠️"}</span></div>}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 16, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedCard.name}</span>
                        <RarityTag rarity={selectedCard.rarity} />
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedCard.set_name}</div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>#{selectedCard.card_number}</div>
                      {selectedCard.card_color && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Color: {selectedCard.card_color}</div>}
                      {(() => {
                        const basePrice = getLatestPrice(selectedCard);
                        const baseSource = getPriceSource(selectedCard);
                        const displayPrice = basePrice > 0 ? basePrice : (ebayData?.avg || 0);
                        const displaySource = basePrice > 0 ? baseSource : (ebayData?.avg ? "eBay" : null);
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                            {displayPrice > 0 ? (
                              <>
                                <span style={{ fontSize: 15, color: "var(--accent)", fontWeight: 700, fontFamily: "JetBrains Mono" }}>{fmtC(displayPrice)}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 8, color: displaySource === "eBay" ? "var(--amber)" : "var(--green)", background: displaySource === "eBay" ? "var(--amber-soft)" : "var(--green-soft)" }}>{displaySource}</span>
                              </>
                            ) : ebayLoading ? (
                              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading price...</span>
                            ) : (
                              <span style={{ fontSize: 15, color: "var(--text-muted)" }}>—</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
                <div className="purchase-form" style={{ flex: 1, minWidth: 0 }}>
                  <div className="form-grid">
                    <div className="input-group"><label className="input-label">Quantity</label><input className="input" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
                    <div className="input-group">
                      <label className="input-label">Purchase Price</label>
                      <div style={{ display: "flex", gap: 6 }}>
                        <select className="input" value={purchaseCurrency} onChange={(e) => setPurchaseCurrency(e.target.value)} style={{ width: 90, flexShrink: 0, fontSize: 13 }}>
                          {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                        </select>
                        <input className="input" type="number" step="0.01" min="0" placeholder="0.00" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} style={{ flex: 1 }} />
                      </div>
                      {purchaseCurrency !== "USD" && purchasePrice && exchangeRates[purchaseCurrency] && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                          ≈ {fmt(parseFloat(purchasePrice) / exchangeRates[purchaseCurrency])} USD
                        </div>
                      )}
                    </div>
                    <div className="input-group"><label className="input-label">Purchase Date</label><input className="input" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} /></div>
                    <div className="input-group"><label className="input-label">Condition</label><select className="input" value={condition} onChange={(e) => setCondition(e.target.value)}>{["Mint", "Near Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Damaged"].map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                    <div className="input-group"><label className="input-label">Language</label><select className="input" value={language} onChange={(e) => setLanguage(e.target.value)}>{["English", "Japanese", "Chinese", "Korean", "French", "German", "Spanish", "Italian", "Portuguese"].map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
                  </div>
                  <div className="input-group"><label className="input-label">Notes (optional)</label><input className="input" placeholder="Any notes about this card..." value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                  <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
                    <button className="btn btn-primary btn-lg" style={{ flex: 1 }} onClick={handleSave}>{editItem ? "Save Changes" : "Add to Collection"}</button>
                    <button className="btn btn-secondary btn-lg" style={{ flex: 1 }} onClick={() => { if (editItem) onNavigate("collection"); else setSelectedCard(null); }}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCardPage;
